/**
 * AchatsDemandesPubliques.js
 * Page ultra professionnelle pour l'achat de demandes publiques COOPER'S
 * Design moderne avec statistiques, filtres avancés et cartes premium
 */

import React from 'react';

import { Card, CardContent } from '../../components/ui/card';

import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { ShoppingCart, CheckCircle, Building2, Mail, Phone, MapPin, FileText, CreditCard, Eye, Sparkles, Recycle, AlertTriangle, Loader2, MessageSquare, User } from 'lucide-react';

const _API_URL = process.env.REACT_APP_BACKEND_URL;

// V37.28 — Activités professionnelles (doit correspondre à la constante
// `ACTIVITES_PROFESSIONNELLES` de GestionTarifsDemandesPubliques.js).
const ACTIVITES_PROFESSIONNELLES = [
  { value: 'btp', label: 'BTP / Construction', icon: '🏗️' },
  { value: 'commerce', label: 'Commerce / Distribution', icon: '🏪' },
  { value: 'industrie', label: 'Industrie / Manufacturing', icon: '🏭' },
  { value: 'agriculture', label: 'Agriculture / Élevage', icon: '🌾' },
  { value: 'services', label: 'Services / Prestations', icon: '🛠️' },
  { value: 'transport', label: 'Transport / Logistique', icon: '🚚' },
  { value: 'restauration', label: 'Restauration / Hôtellerie', icon: '🍽️' },
  { value: 'sante', label: 'Santé / Médico-social', icon: '⚕️' },
  { value: 'education', label: 'Éducation / Formation', icon: '🎓' },
  { value: 'numerique', label: 'Numérique / IT', icon: '💻' },
  { value: 'artisanat', label: 'Artisanat', icon: '🎨' },
  { value: 'tourisme', label: 'Tourisme / Loisirs', icon: '🏝️' },
  { value: 'energie', label: 'Énergie / Environnement', icon: '⚡' },
  { value: 'immobilier', label: 'Immobilier', icon: '🏢' },
  { value: 'finance', label: 'Finance / Assurance', icon: '💰' },
  { value: 'autre', label: 'Autre', icon: '📋' },
];

// V37.28 — Extrait l'array d'activites d'une demande (compat retro string)
const _getDemandeActivites = (d) => {
  if (Array.isArray(d?.activites) && d.activites.length > 0) {
    return d.activites.map(v => (v || '').toString().toLowerCase().trim()).filter(Boolean);
  }
  const raw = (d?.secteur || '').toString().trim().toLowerCase();
  if (!raw || raw === 'general') return [];
  return raw.split(/[,;|]/).map(s => s.trim()).filter(Boolean);
};

export default function AchatsModals({ ctx }) {
  const { formatMontant, handleAchat, remiseEligibility, remiseLoading, remiseMotif, remiseNote, remiseSubmitting, selectedDemande, setRemiseMotif, setRemiseNote, setShowAchatModal, setShowDetailsModal, setShowRemiseModal, showAchatModal, showDetailsModal, showRemiseModal, submitRemise, tarifInfo, walletBalance } = ctx;
  return (
    <>
      {/* Modal Achat */}
      <Dialog open={showAchatModal} onOpenChange={setShowAchatModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <ShoppingCart className="h-6 w-6 text-indigo-600" />
              Confirmer l'achat
            </DialogTitle>
            <DialogDescription>
              Choisissez votre mode de paiement pour accéder aux coordonnées
            </DialogDescription>
          </DialogHeader>
          
          {selectedDemande && (
            <div className="space-y-4 py-4">
              <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-indigo-900">{selectedDemande.nom_entreprise || 'Entreprise'}</p>
                      <p className="text-sm text-indigo-600">{selectedDemande.type_demande || selectedDemande.type}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-indigo-600">
                        {formatMontant(tarifInfo?.prix_ttc || 12.48)}
                      </p>
                      <p className="text-xs text-indigo-400">Global</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="space-y-3">
                <Button 
                  onClick={() => handleAchat(selectedDemande, 'carte')}
                  className="w-full h-14 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                  data-testid="achat-carte-btn"
                >
                  <CreditCard className="h-5 w-5 mr-3" />
                  <div className="text-left">
                    <p className="font-semibold">Payer par carte (CB)</p>
                    <p className="text-xs text-indigo-200">Accès immédiat aux coordonnées</p>
                  </div>
                </Button>

                {(() => {
                  // Priorité : prix spécifique à la demande, sinon tarif global, sinon défaut.
                  const prixCredits = (
                    selectedDemande?.prix_credits ??
                    tarifInfo?.prix_credits ??
                    5
                  );
                  const suffisant = walletBalance >= prixCredits;
                  return (
                    <Button
                      onClick={() => handleAchat(selectedDemande, 'credits')}
                      disabled={!suffisant}
                      className={`w-full h-14 ${suffisant
                        ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600'
                        : 'bg-slate-200 text-slate-500 cursor-not-allowed'}`}
                      data-testid="achat-credits-btn"
                    >
                      <Sparkles className="h-5 w-5 mr-3" />
                      <div className="text-left">
                        <p className="font-semibold">
                          Payer avec mes crédits — {prixCredits} UC
                        </p>
                        <p className={`text-xs ${suffisant ? 'text-amber-50' : 'text-red-600'}`}>
                          {suffisant
                            ? `Solde après achat : ${walletBalance - prixCredits} crédits`
                            : `Solde insuffisant — ${walletBalance} / ${prixCredits} crédits`}
                        </p>
                      </div>
                    </Button>
                  );
                })()}

                <p className="text-[11px] text-center text-slate-500 pt-1">
                  <strong>UC</strong> = Unités de Crédit du wallet ESS · Solde actuel :{' '}
                  <strong className="text-amber-600">{walletBalance} crédits</strong>
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Détails */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-emerald-600" />
              Détails de la demande
            </DialogTitle>
          </DialogHeader>
          
          {selectedDemande && (
            <div className="space-y-4 py-4">
              <Card className="bg-emerald-50 border-emerald-300">
                <CardContent className="p-4 space-y-3" data-testid="achat-details-card">
                  {/* V37.19 — Raison sociale (entreprise) */}
                  <div className="flex items-start gap-3">
                    <Building2 className="h-5 w-5 text-emerald-700 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] uppercase tracking-wider text-emerald-900 font-bold">Raison sociale</p>
                      <p className="font-semibold text-emerald-950 truncate">
                        {selectedDemande.demande?.raison_sociale
                          || selectedDemande.demande?.entreprise
                          || selectedDemande.demande?.nom_entreprise
                          || selectedDemande.nom_entreprise
                          || '—'}
                      </p>
                      {selectedDemande.demande?.siret && (
                        <p className="text-[11px] text-emerald-800 font-mono mt-0.5">SIRET · {selectedDemande.demande.siret}</p>
                      )}
                    </div>
                  </div>

                  {/* V37.19 — Nom + Prénom du contact */}
                  <div className="flex items-start gap-3">
                    <User className="h-5 w-5 text-emerald-700 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] uppercase tracking-wider text-emerald-900 font-bold">Contact</p>
                      <p className="font-semibold text-emerald-950">
                        {[
                          selectedDemande.demande?.prenom || selectedDemande.prenom_contact,
                          selectedDemande.demande?.nom || selectedDemande.nom_contact,
                        ].filter(Boolean).join(' ').trim() || '—'}
                      </p>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex items-start gap-3">
                    <Mail className="h-5 w-5 text-emerald-700 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] uppercase tracking-wider text-emerald-900 font-bold">Email</p>
                      <a
                        href={`mailto:${selectedDemande.demande?.email || selectedDemande.email_contact || ''}`}
                        className="font-semibold text-emerald-950 hover:underline break-all"
                      >
                        {selectedDemande.demande?.email || selectedDemande.email_contact || '—'}
                      </a>
                    </div>
                  </div>

                  {/* Téléphone */}
                  <div className="flex items-start gap-3">
                    <Phone className="h-5 w-5 text-emerald-700 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] uppercase tracking-wider text-emerald-900 font-bold">Téléphone</p>
                      <a
                        href={`tel:${selectedDemande.demande?.telephone || selectedDemande.telephone_contact || ''}`}
                        className="font-semibold text-emerald-950 hover:underline"
                      >
                        {selectedDemande.demande?.telephone || selectedDemande.telephone_contact || '—'}
                      </a>
                    </div>
                  </div>

                  {/* V37.19 — Adresse postale COMPLÈTE (rue + CP + ville + territoire) */}
                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-emerald-700 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] uppercase tracking-wider text-emerald-900 font-bold">Adresse postale</p>
                      {(() => {
                        const rue = selectedDemande.demande?.adresse || selectedDemande.adresse_contact || '';
                        const cp = selectedDemande.demande?.code_postal || '';
                        const ville = selectedDemande.demande?.ville || '';
                        const territoire = selectedDemande.demande?.territoire || '';
                        const cpVille = [cp, ville].filter(Boolean).join(' ');
                        const lines = [rue, cpVille, territoire].filter(Boolean);
                        if (lines.length === 0) {
                          return <p className="font-semibold text-emerald-950">—</p>;
                        }
                        return lines.map((line, i) => (
                          <p key={i} className="font-semibold text-emerald-950 leading-snug">{line}</p>
                        ));
                      })()}
                    </div>
                  </div>

                  {/* V37.19 — Message du client (récap, si dispo) */}
                  {selectedDemande.demande?.message && (
                    <div className="flex items-start gap-3 pt-3 border-t border-emerald-200">
                      <MessageSquare className="h-5 w-5 text-emerald-700 flex-shrink-0 mt-0.5" />
                      <div className="min-w-0 flex-1">
                        <p className="text-[10px] uppercase tracking-wider text-emerald-900 font-bold">Message du client</p>
                        <p className="text-sm text-slate-900 whitespace-pre-wrap leading-relaxed font-medium mt-1">
                          {selectedDemande.demande.message}
                        </p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              <DialogFooter className="flex-col sm:flex-row gap-2">
                {/* V37.20 — Copier la fiche complète au presse-papier */}
                <Button
                  data-testid="copy-fiche-complete"
                  variant="outline"
                  onClick={async () => {
                    try {
                      const d = selectedDemande.demande || {};
                      const raisonSociale = d.raison_sociale || d.entreprise || d.nom_entreprise || selectedDemande.nom_entreprise || '';
                      const prenom = d.prenom || selectedDemande.prenom_contact || '';
                      const nom = d.nom || selectedDemande.nom_contact || '';
                      const email = d.email || selectedDemande.email_contact || '';
                      const telephone = d.telephone || selectedDemande.telephone_contact || '';
                      const adresse = d.adresse || selectedDemande.adresse_contact || '';
                      const codePostal = d.code_postal || '';
                      const ville = d.ville || '';
                      const territoire = d.territoire || '';
                      const siret = d.siret || '';
                      const statutJuridique = (d.type_entreprise || '').toString().toUpperCase();
                      const message = d.message || '';

                      // Format structuré KEY: VALUE — facile à parser pour
                      // import dans « Mes Membres » (CooperMembresManagement)
                      const lines = [
                        `Raison sociale: ${raisonSociale}`,
                        `SIRET: ${siret}`,
                        `Statut juridique: ${statutJuridique}`,
                        `Prénom: ${prenom}`,
                        `Nom: ${nom}`,
                        `Email: ${email}`,
                        `Téléphone: ${telephone}`,
                        `Adresse: ${adresse}`,
                        `Code postal: ${codePostal}`,
                        `Ville: ${ville}`,
                        `Territoire: ${territoire}`,
                        `Message: ${message}`,
                      ];
                      const text = lines.filter(l => l.split(': ')[1]).join('\n');

                      if (navigator.clipboard?.writeText) {
                        await navigator.clipboard.writeText(text);
                      } else {
                        // Fallback (HTTP, vieux navigateurs)
                        const ta = document.createElement('textarea');
                        ta.value = text;
                        ta.style.position = 'fixed';
                        ta.style.opacity = '0';
                        document.body.appendChild(ta);
                        ta.select();
                        document.execCommand('copy');
                        document.body.removeChild(ta);
                      }
                      toast.success(
                        'Fiche copiée ! Collez-la dans « Mes Membres → Importer depuis presse-papier ».',
                        { duration: 5000 },
                      );
                    } catch (e) {
                      toast.error(`Copie impossible : ${e.message}`);
                    }
                  }}
                  className="w-full sm:w-auto border-emerald-600 text-emerald-700 hover:bg-emerald-50 font-semibold"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Copier la fiche complète
                </Button>
                <Button onClick={() => setShowDetailsModal(false)} className="w-full sm:flex-1 bg-slate-900 hover:bg-slate-800 text-white">
                  Fermer
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ============================================
          Modal REMISE EN COMMUNITYPLACE
          ============================================ */}
      <Dialog open={showRemiseModal} onOpenChange={setShowRemiseModal}>
        <DialogContent className="max-w-lg" data-testid="remise-communityplace-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Recycle className="h-5 w-5 text-amber-600" />
              Remettre en Communityplace
            </DialogTitle>
            <DialogDescription>
              Cette demande sera retirée de votre espace et rendue disponible pour les autres COOPER&apos;S.
              Le prix d&apos;achat initial n&apos;est <strong>pas remboursé</strong>.
            </DialogDescription>
          </DialogHeader>

          {remiseLoading && (
            <div className="flex items-center justify-center py-8 text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />Calcul de l&apos;éligibilité…
            </div>
          )}

          {!remiseLoading && remiseEligibility && (
            <div className="space-y-4">
              {/* Eligibility recap */}
              <div className={`rounded-lg border p-3 ${
                remiseEligibility.eligible ? 'border-emerald-200 bg-emerald-50' : 'border-rose-200 bg-rose-50'
              }`}>
                <div className="flex items-start gap-2">
                  {remiseEligibility.eligible
                    ? <CheckCircle className="h-5 w-5 text-emerald-600 mt-0.5" />
                    : <AlertTriangle className="h-5 w-5 text-rose-600 mt-0.5" />}
                  <div className="text-sm">
                    {remiseEligibility.eligible ? (
                      <>
                        <p className="font-semibold text-emerald-900">Éligible à la remise</p>
                        <p className="text-emerald-700 text-xs mt-0.5">
                          Quota mensuel : {remiseEligibility.remises_used_30d}/{remiseEligibility.remises_max_30d} utilisées
                          ({remiseEligibility.remises_remaining_30d} restantes ce mois).
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="font-semibold text-rose-900">Quota atteint</p>
                        <p className="text-rose-700 text-xs mt-0.5">
                          {remiseEligibility.remises_used_30d}/{remiseEligibility.remises_max_30d} remises utilisées sur 30 jours.
                          Patientez avant la prochaine.
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Pénalité */}
              <div className="rounded-lg border border-slate-200 p-3 bg-slate-50">
                <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-2">
                  Délai &amp; pénalité
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-xs text-slate-500">Achat il y a</div>
                    <div className="font-bold text-slate-900">{remiseEligibility.days_since_purchase} jour{remiseEligibility.days_since_purchase > 1 ? 's' : ''}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">Délai sans pénalité</div>
                    <div className="font-bold text-slate-900">{remiseEligibility.grace_days} jours</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">Jours hors délai</div>
                    <div className={`font-bold ${remiseEligibility.days_over_grace > 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
                      {remiseEligibility.days_over_grace} j
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">Pénalité totale</div>
                    <div className={`font-bold ${remiseEligibility.penalty_credits_total > 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
                      {remiseEligibility.penalty_credits_total} crédits
                    </div>
                  </div>
                </div>
                {remiseEligibility.penalty_credits_total > 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-200 text-xs">
                    <p className="text-slate-600">
                      Tarif : <strong>{remiseEligibility.penalty_credits_per_day} crédits/jour</strong> au-delà de {remiseEligibility.grace_days} jours.
                    </p>
                    <p className={`mt-1 ${remiseEligibility.wallet_sufficient ? 'text-emerald-700' : 'text-rose-700'}`}>
                      Solde wallet : <strong>{remiseEligibility.wallet_balance}</strong> crédits — {remiseEligibility.wallet_sufficient ? 'Suffisant ✓' : 'INSUFFISANT ✗'}
                    </p>
                    {!remiseEligibility.wallet_sufficient && (
                      <div className="mt-2 p-2 bg-rose-100 border border-rose-200 rounded-md">
                        <p className="text-rose-800 text-xs">
                          Il vous manque <strong>{remiseEligibility.penalty_credits_total - remiseEligibility.wallet_balance} crédits</strong> pour couvrir la pénalité.
                        </p>
                        <a
                          href="/cooper/credits"
                          className="inline-block mt-1 text-rose-700 underline font-semibold text-xs hover:text-rose-900"
                        >
                          Recharger mes crédits →
                        </a>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Motif */}
              <div>
                <label className="text-[10px] uppercase tracking-widest font-medium text-slate-500">Motif de remise *</label>
                <Select value={remiseMotif} onValueChange={setRemiseMotif}>
                  <SelectTrigger className="mt-1" data-testid="remise-motif-select"><SelectValue placeholder="Choisir un motif…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hors_zone">Hors de ma zone géographique</SelectItem>
                    <SelectItem value="trop_eloigne">Distance trop importante</SelectItem>
                    <SelectItem value="indisponibilite">Indisponibilité ponctuelle</SelectItem>
                    <SelectItem value="conflit_calendrier">Conflit de calendrier</SelectItem>
                    <SelectItem value="profil_inadapte">Profil entreprise inadapté à mes compétences</SelectItem>
                    <SelectItem value="client_injoignable">Client injoignable</SelectItem>
                    <SelectItem value="autre">Autre (préciser dans la note)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Note */}
              <div>
                <label className="text-[10px] uppercase tracking-widest font-medium text-slate-500">Note interne (optionnelle)</label>
                <Input
                  value={remiseNote}
                  onChange={(e) => setRemiseNote(e.target.value)}
                  placeholder="Précisions à destination du Super Admin"
                  className="mt-1"
                  data-testid="remise-note-input"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRemiseModal(false)} disabled={remiseSubmitting}>Annuler</Button>
            <Button
              onClick={submitRemise}
              disabled={
                remiseSubmitting || remiseLoading || !remiseMotif ||
                !remiseEligibility?.eligible ||
                (remiseEligibility?.penalty_credits_total > 0 && !remiseEligibility?.wallet_sufficient)
              }
              className="bg-amber-700 hover:bg-amber-800 text-white"
              data-testid="confirm-remise-btn"
            >
              {remiseSubmitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Recycle className="h-4 w-4 mr-2" />}
              Confirmer la remise
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
